import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { DSSetup, PopupData, Service, ValidationFunction } from 'webapp-types';
import { TeamSectionItemType } from 'webapp/enums/enums';

import CommonMetricAttributesUnitFieldsetComponent from 'webapp/components/common/metric-attributes-unit-fieldset';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormSelectComponent from 'webapp/components/form/select';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import type { Args } from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import MetricSetup from 'webapp/models-custom/metric-setup';
import type TeamSection from 'webapp/models-custom/team-section';

export default class TeamsViewSectionPopupAddItemPopupComponent extends PopupInnerModalComponent<PopupData.Teams.SectionItem.Add> {
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/metrics')
	declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked spaceGoals: LabelValuePairString[] = [];
	@tracked selectedGoal = '';

	private validateFieldset: null | ValidationFunction = null;
	private metricSetup: MetricSetup;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const { metricSetupData } = this.popupData;

		this.metricSetup = new MetricSetup({ ...metricSetupData });

		if (this.isMetric) {
			void this.mmMetricsService.prepareForMetricEditing();
		} else if (this.isGoal) {
			this.refreshSpaceGoals();
		}
	}

	get metricSetupData() {
		return (
			this.metricSetup.metricSetupData || {
				key: '',
				spaceAccessId: null,
			}
		);
	}

	get sectionItemType(): TeamSectionItemType {
		return this.popupData.type;
	}

	get section(): TeamSection {
		return this.popupData.model;
	}

	get isSubmitDisabled(): boolean {
		if (this.isMetric) {
			return !this.metricSetup.metricData;
		} else {
			return !this.selectedGoal;
		}
	}

	get isMetric(): boolean {
		return this.sectionItemType === TeamSectionItemType.METRIC;
	}

	get isGoal(): boolean {
		return this.sectionItemType === TeamSectionItemType.GOAL;
	}

	get typeOptions(): {
		name: string;
		icon: IconName;
		skipGoals?: boolean;
		legend: string;
		placeholder: string;
	} {
		if (this.isMetric) {
			return {
				name: 'metric',
				icon: 'Metrics',
				skipGoals: true,
				legend: 'Select a metric you would like to add to the section:',
				placeholder: 'Select metric',
			};
		} else {
			return {
				name: 'goal',
				icon: 'Target',
				legend: 'Select a goal you would like to add to the section:',
				placeholder: 'Select goal',
			};
		}
	}

	get buttonText(): string {
		return this.popupData.actionText || 'Add to section';
	}

	refreshSpaceGoals(): void {
		this.spaceGoals = this.mmGoalsService.goalForSpaceModels.map((goal) => {
			return {
				avatar: goal.sourceCM?.realAvatar,
				value: goal.id!,
				label: goal.title,
				label2: goal.source?.title,
			};
		});
	}

	@action
	createNewGoal(): void {
		this.mmGoalsService.openNewPopup('homepage', (model) => {
			this.refreshSpaceGoals();
			this.changeGoal(model.id!);
		});
	}

	@action
	changeGoal(newGoalId: string): void {
		this.selectedGoal = newGoalId;
	}

	@action
	changeMetricSetupData(value: DSSetup.MetricSetupData): void {
		this.metricSetup.setFromMetricSetupData(value);
	}

	@action
	bindFieldsetValidation(validationFn: ValidationFunction) {
		this.validateFieldset = validationFn;
	}

	@action
	save(): void {
		if (this.submitInProgress) {
			return;
		}

		const isMetric = this.isMetric;
		if (
			isMetric &&
			this.validateFieldset &&
			Object.keys(this.validateFieldset()).length !== 0
		) {
			return;
		}

		this.submitInProgress = true;
		if (isMetric && this.popupData.onSubmit) {
			this.popupData.onSubmit(this.metricSetupData);
			this.submitInProgress = false;
			this.close();
		} else {
			this.section
				.addItem(TeamSectionItemType.GOAL, +this.selectedGoal)
				.then(() => {
					this.close();
				})
				.finally(() => {
					this.submitInProgress = false;
				})
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		}
	}

	<template>
		<div class="title">
			<CommonUiIconComponent @name={{this.typeOptions.icon}} @size="lg" />Select
			{{this.typeOptions.name}}
		</div>

		<div class="content">
			{{#if this.isMetric}}
				<CommonMetricAttributesUnitFieldsetComponent
					@model={{this.metricSetupData}}
					@formOptions={{hash legend=this.typeOptions.legend}}
					@changeMetricSetupData={{this.changeMetricSetupData}}
					@requireSingleOutput={{true}}
					@validationBinder={{this.bindFieldsetValidation}}
					@onContentChange={{this.scheduleReposition}}
					@isDisabled={{this.submitInProgress}}
				/>

			{{else}}
				<h4>{{this.typeOptions.legend}}</h4>
				<div class="row">
					<FormSelectComponent
						name="goal"
						@placeholder={{this.typeOptions.placeholder}}
						@selected={{this.selectedGoal}}
						@options={{this.spaceGoals}}
						@isNumeric={{false}}
						@isDisabled={{this.submitInProgress}}
						@onChange={{this.changeGoal}}
						@maxOptionsShown={{this.spaceGoals.length}}
						@extraClass="goal-select"
					/>
				</div>

				<div class="row">
					<a role="button" class="link link--blue" {{on "click" this.createNewGoal}}>
						+ New goal
					</a>
				</div>
			{{/if}}

			<div class="controls2">
				<SubmitButtonComponent
					@text="Cancel"
					@theme="grey-empty"
					@noDelay={{true}}
					@onTrigger={{this.close}}
				/>
				<SubmitButtonComponent
					@text={{this.buttonText}}
					@extraClass="save"
					@onTrigger={{this.save}}
					@isDisabled={{this.isSubmitDisabled}}
					@submitInProgress={{this.submitInProgress}}
				/>
			</div>
		</div>
	</template>
}
